import { Button, Col, Row, Space } from 'antd'
import { useEffect } from 'react'

import { magnifyBlackSVG } from 'assets/svg/magnify'
import { ButtonIcon } from 'atom/button'
import { LineBreak } from 'atom/line-break'

import MultiListingTitleSelector from 'components/redesign/MultiListingIdTitleSelector'
import { LocationSelector } from 'components/redesign/location-selector'
import { MultiTenantSelector } from 'components/redesign/multiple-tenant-selector'
import { ReusableSelector } from 'components/reusableSelector/selector'

import { useGeneralTranslation } from 'hooks/translation/useGeneralTranslation'
import { useSelectorTranslation } from 'hooks/translation/useSelectorTranslation'
import { useSelector } from 'hooks/useSelector'
import { useTagging } from 'hooks/useTagging'

import { TenantFilter } from 'models/filter'
import { ListingRef } from 'models/listing'
import { MasterAccountRef } from 'models/master-account'
import { TenantRef } from 'models/tenant'

import { useAppState } from 'state'

interface TenantsAllSelectorsProps {
  loading: boolean
  filter: TenantFilter
  tenants: TenantRef[]
  dropdownClassName?: string
  fetchData: () => void
  onFilterChange: (filter: TenantFilter, tenants?: TenantRef[]) => void
  onLocationChange: (chosenCountries: string[], chosenProvinces: string[]) => void
}

const TenantsAllSelectors: React.FC<TenantsAllSelectorsProps> = ({
  loading,
  filter,
  tenants,
  dropdownClassName,
  onFilterChange,
  fetchData,
  onLocationChange,
}) => {
  const { IsDesktop, IsLaptop, isMockUpEnabled, language } = useAppState()
  const isDesktop = IsDesktop()
  const isLaptop = IsLaptop()
  const { currentUser } = useAppState()
  const { accountNamesText } = useSelectorTranslation()

  const { getTaggingsWithoutPagination } = useTagging()

  const isAdmin = currentUser?.role === 'admin'

  useEffect(() => {
    getTaggingsWithoutPagination('Tenant')
  }, [])

  const {
    multiMasterAccountDebounceFetcher,
    multiMasterAccountHandleChange,
    multiMasterAccountHandlePlaceholder,
    multiMasterAccountHandleValue,
    renderMultiMasterAccountOption,
  } = useSelector()

  const handleMasterAccountSelection = (masterAccounts: MasterAccountRef[]) =>
    onFilterChange({
      masterAccounts: masterAccounts.filter((obj, index) => {
        return index === masterAccounts.findIndex((o) => obj.id === o.id)
      }),
    })

  const { searchText } = useGeneralTranslation()
  const renderMultiTenantSelector = () => (
    <MultiTenantSelector
      onOptionsChange={(tenants) => onFilterChange(filter, tenants)}
      onClear={() => onFilterChange(filter)}
      longerWidth={true}
      isCombineRight={true}
      loading={loading}
      dropdownClassName={dropdownClassName}
    />
  )

  const renderLocationSelector = () => (
    <LocationSelector
      dropdownClassName="tenant-page"
      transparent
      onSearch={onLocationChange}
      loading={loading}
      removeSVG={true}
      customWidth={145}
    />
  )

  const renderMasterAccountSelector = () => (
    <ReusableSelector
      maxTagCount={0}
      showArrow={false}
      showSearch={true}
      isSingle={false}
      showMagnifySVG={false}
      isDebounceFetcher={true}
      combineType="both"
      fieldForValue="id"
      placeholder={accountNamesText}
      dropdownClassName={dropdownClassName}
      onClear={() => onFilterChange({ ...filter, masterAccounts: [] })}
      onOptionsChange={(opts: MasterAccountRef[]) => handleMasterAccountSelection(opts)}
      debounceFetcher={multiMasterAccountDebounceFetcher}
      handlePlaceholder={multiMasterAccountHandlePlaceholder}
      handleValue={multiMasterAccountHandleValue}
      handleOptionChange={multiMasterAccountHandleChange}
      renderOption={renderMultiMasterAccountOption}
      isMockUpEnabled={isMockUpEnabled}
    />
  )

  const renderMultiListingTitleSelector = () => (
    <MultiListingTitleSelector
      dropdownClassName={dropdownClassName}
      onOptionChange={(listingsResp: ListingRef[]) => onFilterChange({ ...filter, listings: listingsResp })}
      setClear={() => onFilterChange(filter)}
      tenants={tenants}
      isCombineLeftSelector
      isCombineSelector={isAdmin}
      width={isDesktop || isLaptop ? (language === 'en' ? '120px' : '220px') : '100%'}
      minWidth={120}
    />
  )

  return (
    <>
      {isDesktop || isLaptop ? (
        <Space wrap size={[0, 6]} style={{ display: 'flex' }}>
          {renderMultiTenantSelector()}
          <LineBreak />
          {renderLocationSelector()}
          {<LineBreak />}
          {renderMasterAccountSelector()}
          {<LineBreak />}
          {renderMultiListingTitleSelector()}

          <ButtonIcon
            icon={magnifyBlackSVG}
            loading={loading}
            disabled={loading}
            fetchData={fetchData}
            withinSearchBar
          />
        </Space>
      ) : (
        <Row wrap gutter={[6, 6]}>
          <Col xs={24} sm={24} md={24}>
            {renderMultiTenantSelector()}
          </Col>
          <Col xs={24} sm={24} md={24}>
            <LocationSelector onSearch={onLocationChange} loading={loading} />
          </Col>
          {isAdmin && (
            <Col xs={24} sm={24} md={24}>
              {renderMasterAccountSelector()}
            </Col>
          )}
          <Col xs={24} sm={24} md={24}>
            {renderMultiListingTitleSelector()}
          </Col>
          <Col xs={24} sm={24} md={24}>
            <Button block type="primary" loading={loading} disabled={loading} onClick={fetchData}>
              {searchText}
            </Button>
          </Col>
        </Row>
      )}
    </>
  )
}

export default TenantsAllSelectors
